import type { ReactNode } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { useLocale } from "@/context/LanguageContext";
import type { AnalyticsBreakdownPoint } from "@/graphql/generated";
import { ChartCard, ChartEmpty, ChartSkeleton } from "./ChartCard";
import { SuppressibleTable } from "./SuppressibleTable";
import { formatCompactNumber } from "../utils/dateRange";

interface Props {
  data: AnalyticsBreakdownPoint[];
  loading?: boolean;
  action?: ReactNode;
  valueHeader: string;
}

export function AnalyticsBreakdownChart({ data, loading = false, action, valueHeader }: Props) {
  const { t } = useLocale();
  const title = t("analytics.breakdown.title");

  if (loading && data.length === 0) {
    return (
      <ChartCard title={title} action={action}>
        <ChartSkeleton />
      </ChartCard>
    );
  }
  if (data.length === 0) {
    return (
      <ChartCard title={title} action={action}>
        <ChartEmpty />
      </ChartCard>
    );
  }

  const anySuppressed = data.some((p) => p._suppressed);
  const chartData = data.map((p) => ({
    label: p.key,
    count: p._suppressed ? null : p.count ?? 0,
  }));

  return (
    <ChartCard title={title} action={action}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
          <XAxis
            dataKey="label"
            fontSize={11}
            tickLine={false}
            axisLine={false}
            tick={{ fill: "var(--color-muted-foreground)" }}
          />
          <YAxis
            fontSize={11}
            tickLine={false}
            axisLine={false}
            tick={{ fill: "var(--color-muted-foreground)" }}
            tickFormatter={formatCompactNumber}
            allowDecimals={false}
          />
          <Tooltip
            cursor={{ fill: "var(--color-muted)" }}
            contentStyle={{
              backgroundColor: "var(--color-card)",
              border: "1px solid var(--color-border)",
              borderRadius: "8px",
              color: "var(--color-foreground)",
              fontSize: 12,
            }}
            formatter={(value) => [Number(value).toLocaleString(), t("analytics.breakdown.tooltip")]}
          />
          <Bar
            dataKey="count"
            name={t("analytics.breakdown.tooltip")}
            fill="var(--color-primary)"
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
      <SuppressibleTable
        labelHeader={valueHeader}
        rows={data.map((p) => ({ label: p.key, count: p.count, suppressed: !!p._suppressed }))}
      />
      {anySuppressed && (
        <p className="mt-3 text-xs text-muted-foreground">{t("analytics.suppressionNote")}</p>
      )}
    </ChartCard>
  );
}
